import React, { useState } from 'react';
import { User, Mail, Phone as PhoneIcon, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { mockServices } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const SimpleBookingSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    model: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to backend API
      const response = await axios.post(`${API}/bookings`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        model: formData.model || null,
        message: formData.message || null
      });

      console.log('Booking created:', response.data);
      
      // Reset form first
      const resetData = {
        name: '',
        email: '',
        phone: '',
        service: '',
        model: '',
        message: ''
      };
      setFormData(resetData);
      
      // Show success message
      toast.success('✅ Booking Confirmed! We\'ll call you within 1 hour.', {
        duration: 5000,
        style: {
          background: '#06b6d4',
          color: 'white',
          fontWeight: 'bold'
        }
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again or call us at 905-699-6599');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Book Your <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Repair</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Fill out the form and we&apos;ll call you back within 1 hour. Or walk in anytime!
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Card className="border-2 border-slate-200 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 border-b border-slate-200">
              <CardTitle className="text-2xl text-slate-900">Request Callback</CardTitle>
              <CardDescription className="text-base">
                We&apos;ll contact you to schedule your repair
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 font-semibold">
                      Full Name *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                      <Input
                        id="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10 h-12 border-slate-300 focus:border-cyan-500"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-semibold">
                      Email Address *
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10 h-12 border-slate-300 focus:border-cyan-500"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-semibold">
                      Phone Number *
                    </Label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-10 h-12 border-slate-300 focus:border-cyan-500"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service" className="text-slate-700 font-semibold">
                      Service Needed *
                    </Label>
                    <Select 
                      value={formData.service}
                      onValueChange={(value) => setFormData({ ...formData, service: value })}
                      disabled={isSubmitting}
                    >
                      <SelectTrigger className="h-12 border-slate-300 focus:border-cyan-500">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {mockServices.map((service) => (
                          <SelectItem key={service.id} value={service.name}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="model" className="text-slate-700 font-semibold">
                      iPhone Model (Optional)
                    </Label>
                    <Input
                      id="model"
                      placeholder="e.g., iPhone 15 Pro"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="h-12 border-slate-300 focus:border-cyan-500"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700 font-semibold">
                    Additional Notes (Optional)
                  </Label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <Textarea
                      id="message"
                      placeholder="Describe the issue..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="pl-10 min-h-24 border-slate-300 focus:border-cyan-500"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Callback'}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  Or call us directly at <a href="tel:9056996599" className="text-cyan-600 font-semibold">905-699-6599</a>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default SimpleBookingSection;
